import { useMutation } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { deleteEngageDomain as deleteEngageDomainApi } from 'api/engage';
import { GivebutterFormProvider, SubmitButton, useGivebutterForm } from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { EngageDomain } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface DeleteDomainDialogProps extends AlertDialog.AlertDialogRootProps {
    domain: EngageDomain;
}

const DeleteDomainDialog = (props: DeleteDomainDialogProps) => {
    const toast = useToast();
    const { domain, children, onOpenChange, ...rest } = props;
    const { accountId } = useCurrentAccount();
    const methods = useGivebutterForm();
    const { handleSubmit } = methods;
    const { mutate: submit } = useMutation({
        mutationFn: () => deleteEngageDomainApi(accountId, domain.id),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageDomains(accountId),
                type: 'all',
            });
            toast.success(<>Successfully deleted domain</>);
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(<>{error.response?.data.message ?? 'Unknown error deleting domain'}</>);
        },
        onSettled: () => {
            onOpenChange?.(false);
        },
    });

    return (
        <AlertDialog.Root type={'error'} onOpenChange={onOpenChange} {...rest}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Delete domain?</AlertDialog.Title>
                        <AlertDialog.Description>
                            This action will prevent you from sending emails with {domain.value}.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>

                        <GivebutterFormProvider {...methods}>
                            <form className="w-full" onSubmit={handleSubmit(() => submit())}>
                                <SubmitButton className="!w-1/2 !border-none !bg-red-500">
                                    Delete
                                </SubmitButton>
                            </form>
                        </GivebutterFormProvider>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

export { DeleteDomainDialog };
